import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Box,
  BoxProps,
  Button,
  Card,
  Container,
  Fade,
  Grid,
  Stack,
  StackProps,
  SxProps,
  Tooltip,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';

import {
  ISubmissionQuestion,
  getSubmissionById,
  getSubmissionQuestion,
  getSubmissionQuestionAIExplanation,
  getSubmissionState,
  resetSubmission,
} from '../../../store/SubmissionSlice';
import {
  ISubTopic,
  getSubTopicById,
  getSubTopicState,
} from '../../../store/SubTopicSlice';
import { getAuthState } from '../../../store/AuthSlice';
import {
  getDiagnosticBySubmissionId,
  getDiagnosticState,
} from '../../../store/DiagnosticSlice';
import { getTopicsWithSubTopics, ITopic } from '../../../store/TopicSlice';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import Breadcrumbs, {
  BreadcrumbOption,
  getBreadCrumbOptionsByTopicAndSubTopicData,
} from '../../../components/BreadCrumb/Breadcrumbs';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import QuestionListContainer from './components/QuestionListContainer';
import QuestionDetailViewer from './components/QuestionDetailViewer';
import DetailedMarkingSchemeViewer from './components/DetailedMarkingSchemeViewer';
import CompletionCard from './components/CompletionCard';
import { getUrlConvertedString, msToMinutesSeconds } from '../../../helper';
import LeadGenView from './components/LeadGenView';
import { getAccessLimitState } from '../../../store/AccessLimitSlice';
import PremiumView from './components/PremiumView';
import GradedSection from './components/GradedSection';

const stackProps: StackProps = {
  gap: 1,
  padding: '16px',
  borderRadius: '16px',
};

const titleTypoProps: TypographyProps = {
  fontSize: '16px',
  lineHeight: '19px',
};

const boxProps: BoxProps = {
  display: 'flex',
  gap: 1,
  alignItems: 'center',
};

const subTitleTypoProps: TypographyProps = {
  fontSize: '28px',
  fontWeight: '700',
  lineHeight: '34px',
  color: TP_COLORS.gray.darken,
};

const cardStyles: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  padding: '30px',
  borderRadius: '16px',
  border: '1px solid #E2E8F0',
};

const averageBoxProps: BoxProps = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  padding: 2,
  borderRadius: '10px',
};

const gradientBoxStyles: SxProps = {
  position: 'relative',
  borderRadius: '10px',
  p: '16px',
  color: '#334155',
  background: `linear-gradient(90deg, rgba(96, 202, 199, 0.1) 0%, rgba(86, 103, 202, 0.1) 25%, rgba(177, 105, 117, 0.1) 50%, rgba(219, 125, 58, 0.1) 75%, rgba(104, 177, 122, 0.1) 100%)`,
  overflow: 'hidden',
  zIndex: 1,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '10px',
    padding: '1px',
    background: `linear-gradient(90deg, #60CAC7 0%, #5667CA 25%, #B16975 50%, #DB7D3A 75%, #68B17A 100%)`,
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'destination-out',
    maskComposite: 'exclude',
    zIndex: -1,
  },
};

const actionButtonStyles: SxProps = {
  backgroundColor: '#01A79F',
  color: TP_COLORS.white,
  borderRadius: '30px',
  boxShadow: 'rgb(1, 115, 109) 1px 3px 0px',
  width: '190px',
  maxWidth: '100%',
};

const infoIconStyles: SxProps = { fontSize: 15, color: '#334155' };

const fadeMask =
  'linear-gradient(to bottom, rgba(0, 0, 0, 0.2) 10%, rgba(0, 0, 0, 0) 70%)';

const constructBreadcrumbOptions = (
  topicData?: ITopic,
  subTopicData?: ISubTopic
) => {
  const options: Array<BreadcrumbOption> = getBreadCrumbOptionsByTopicAndSubTopicData(
    topicData,
    subTopicData
  );

  options.push({
    type: 'text',
    title: 'Quiz',
    highlight: true,
  });

  return options;
};

const getAverageScore = (subject: string): number => {
  switch (subject) {
    case 'Mathematics':
    case 'MathematicsNew':
    case 'Geography':
    case 'History':
      return 65;
    case 'Economics':
      return 64;
    case 'Business Studies':
      return 66;
    case 'English Language':
    case 'English Literature':
      return 68;
    case 'Computer Science':
      return 62;
    default:
      return 60;
  }
};

function getAverageTimeBySubTopic(index) {
  const baseTimes = [6.27, 7.12, 7.75];

  let totalMinutes;
  if (index < baseTimes.length) {
    totalMinutes = baseTimes[index];
  } else {
    const increment = 0.5;
    const additionalIndex = index - baseTimes.length;
    totalMinutes =
      baseTimes[baseTimes.length - 1] + increment * (additionalIndex + 1);
  }

  const minutes = Math.floor(totalMinutes);
  const seconds = Math.round((totalMinutes - minutes) * 60);

  return `${minutes}m ${seconds}s`;
}

const containerStyles: SxProps = {
  background: 'linear-gradient(180deg, #01736D 0%, #001615 100%)',
  minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
};

const ViewQuizSubmission: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [selectedQuestion, setSelectedQuestion] = useState<
    ISubmissionQuestion | undefined
  >(undefined);
  const [isLeadGenVisible, setIsLeadGenVisible] = useState(false);
  const [isPremiumVisible, setIsPremiumVisible] = useState(false);

  // Store
  const submissionState = useAppSelector(getSubmissionState);
  const authState = useAppSelector(getAuthState);
  const subTopicState = useAppSelector(getSubTopicState);
  const accessLimitState = useAppSelector(getAccessLimitState);
  const { diagnostic } = useAppSelector(getDiagnosticState);

  const authUser = authState.user;
  const submissionData = submissionState.submission;
  const questionData = submissionState.question;
  const subTopicData = subTopicState.subTopic;
  const quizData =
    submissionData && submissionData.quiz ? submissionData.quiz : undefined;
  const topicData = quizData && quizData.topic ? quizData.topic : undefined;
  const breadcrumbOptions = constructBreadcrumbOptions(topicData, subTopicData);
  const submissionQuestions =
    submissionData && submissionData.questions ? submissionData.questions : [];

  const isDiagnostic =
    (topicData && topicData.name === 'Diagnostic Challenge') || false;

  const isPremiumModalToggleable =
    authUser &&
    (authUser.role.name === 'Student' || authUser.isLeadGenSubmitted);

  const isEnabled = isLeadGenVisible || isPremiumVisible;

  const isOpenEndedQuestion = Boolean(
    selectedQuestion?.isOpenEnded && questionData?.theoryQuestion?.question
  );

  const subTopicAverageTime = getAverageTimeBySubTopic(
    subTopicData?.sortingOrder && subTopicData.sortingOrder > 0
      ? subTopicData.sortingOrder - 1
      : 0
  );

  const correctAnswersCount =
    submissionData?.questions.filter((item) => item.answer?.isCorrect === true)
      .length || 0;

  useEffect(() => {
    if (params.submissionId) {
      dispatch(getSubmissionById(params.submissionId));
    }

    if (params.subtopicId) {
      dispatch(getSubTopicById(params.subtopicId));
    }

    return () => {
      dispatch(resetSubmission());
    };
  }, [params.submissionId]);

  useEffect(() => {
    if (params.submissionId && submissionData && submissionQuestions.length) {
      if (!selectedQuestion) {
        const firstQuestion = submissionQuestions[0];

        setSelectedQuestion(firstQuestion);

        if (firstQuestion && firstQuestion.question) {
          const apiData = {
            submissionId: params.submissionId,
            questionId: firstQuestion.question,
          };
          dispatch(getSubmissionQuestion(apiData));
          if (!firstQuestion.isOpenEnded) {
            dispatch(getSubmissionQuestionAIExplanation(apiData));
          }
        }
      }

      if (selectedQuestion) {
        const index = submissionQuestions.findIndex(
          (questionItem) => questionItem.question === selectedQuestion.question
        );

        if (index > -1) {
          setSelectedQuestion(submissionQuestions[index]);
        }
      }

      if (isDiagnostic) {
        dispatch(getDiagnosticBySubmissionId(params.submissionId));
      }

      if (topicData) {
        dispatch(
          getTopicsWithSubTopics({
            curriculum: topicData.curriculum,
            subject: topicData.subject,
            subjectCode: topicData.subjectCode,
            subjectLevel: topicData.subjectLevel,
          })
        );
      }
    }
  }, [params.submissionId, submissionData]);

  useEffect(() => {
    if (
      !isDiagnostic &&
      accessLimitState.limitation &&
      accessLimitState.usage &&
      accessLimitState.limitation.quizAttempts &&
      accessLimitState.limitation.quizAttempts > 0 &&
      accessLimitState.usage.quizAttempts >=
        accessLimitState.limitation.quizAttempts
    ) {
      if (isPremiumModalToggleable) {
        setIsPremiumVisible(true);
      } else {
        setIsLeadGenVisible(true);
      }
    } else {
      setIsLeadGenVisible(false);
      setIsPremiumVisible(false);
    }
  }, [accessLimitState]);

  const handleQuestionChange = (question: ISubmissionQuestion) => {
    if (params.submissionId) {
      const apiData = {
        submissionId: params.submissionId,
        questionId: question.question,
      };
      dispatch(getSubmissionQuestion(apiData));
      if (!question.isOpenEnded) {
        dispatch(getSubmissionQuestionAIExplanation(apiData));
      }

      setSelectedQuestion(question);
    }
  };

  const gridItemProps = { xs: 6, sm: 4, md: 4, lg: 4 };

  const navigateToReport = () => {
    if (topicData && subTopicData && diagnostic?._id) {
      const urlConvertedCurriculum = getUrlConvertedString(
        topicData.curriculum
      );
      const urlConvertedSubject = getUrlConvertedString(topicData.subject);
      const urlConvertedTopic = getUrlConvertedString(topicData.name);
      const urlConvertedSubTopic = getUrlConvertedString(subTopicData.name);

      navigate(
        `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicData._id}/diagnostic-quiz/${diagnostic._id}`
      );
    }
  };

  return (
    <Box sx={containerStyles}>
      <Container maxWidth="lg">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ padding: '30px 0' }}
        >
          <Breadcrumbs options={breadcrumbOptions} showDropDown={belowMd} />
          {submissionData && isDiagnostic ? (
            <Button
              size="large"
              variant="contained"
              color="primary"
              sx={actionButtonStyles}
              onClick={navigateToReport}
            >
              See Detailed Report
            </Button>
          ) : null}
        </Stack>
        {/* MAIN CONTENT CONTAINER */}
        <div style={{ marginBottom: '30px' }}>
          {submissionData ? (
            <Fade in timeout={600}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={8.5} lg={8}>
                  <Stack gap="34px" minHeight="100vh">
                    <Grid container spacing={4}>
                      <Grid item {...gridItemProps}>
                        <Stack
                          {...stackProps}
                          sx={{
                            color: '#20BC5D',
                            backgroundColor: '#E7F9EE',
                            border: '1px solid #20BC5D',
                          }}
                        >
                          <Typography {...titleTypoProps}>
                            Total Score
                          </Typography>
                          <Box {...boxProps}>
                            <img
                              src={`${process.env.PUBLIC_URL}/images/quiz/cup-star-icon.png`}
                              alt="cup-star-icon"
                              height={34}
                            />
                            <Typography {...subTitleTypoProps}>
                              {submissionData.score}/{submissionData.totalScore}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                      <Grid item {...gridItemProps}>
                        <Stack
                          {...stackProps}
                          sx={{
                            color: '#8630F5',
                            backgroundColor: '#F2E8FF',
                            border: '1px solid #8630F5',
                          }}
                        >
                          <Typography {...titleTypoProps}>
                            Time Taken
                          </Typography>
                          <Box {...boxProps}>
                            <img
                              src={`${process.env.PUBLIC_URL}/images/quiz/timer-icon.png`}
                              alt="timer-icon"
                              height={34}
                            />
                            <Typography {...subTitleTypoProps}>
                              {msToMinutesSeconds(
                                (submissionData.timerEnd || 0) -
                                  (submissionData.timerStart || 0)
                              )}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                      <Grid item {...gridItemProps}>
                        <Stack
                          {...stackProps}
                          sx={{
                            color: '#F57272',
                            backgroundColor: '#FFE8E8',
                            border: '1px solid #F57272',
                          }}
                        >
                          <Typography {...titleTypoProps}>Accuracy</Typography>
                          <Box {...boxProps}>
                            <img
                              src={`${process.env.PUBLIC_URL}/images/quiz/target-icon.png`}
                              alt="target-icon"
                              height={34}
                            />
                            <Typography {...subTitleTypoProps}>
                              {(correctAnswersCount /
                                submissionData.questions.length) *
                                100}
                              %
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    </Grid>

                    <Card
                      sx={{
                        ...cardStyles,
                        ...(isEnabled && {
                          position: 'relative',
                          pointerEvents: 'none',
                          maskImage: fadeMask,
                          WebkitMaskImage: fadeMask,
                        }),
                      }}
                    >
                      <Typography fontSize="18px" fontWeight="700">
                        Areas of strengths and improvements
                      </Typography>
                      <Typography fontSize="14px">
                        💪🏼 {submissionData.aiFeedback?.strength || ''}
                      </Typography>
                      <Typography fontSize="14px">
                        🛠️ {submissionData.aiFeedback?.improvement || ''}
                      </Typography>
                    </Card>

                    {isEnabled && (
                      <Box
                        sx={{
                          position: 'absolute',
                          zIndex: 10,
                          marginTop: { xs: '240px', sm: '120px', md: '100px' },
                        }}
                      >
                        {isPremiumVisible && <PremiumView />}
                        {isLeadGenVisible && <LeadGenView />}
                      </Box>
                    )}

                    {!isEnabled && (
                      <>
                        <Card sx={cardStyles}>
                          <Typography fontSize="18px" fontWeight="700">
                            Examiner Recommendation & Competitive Benchmark
                          </Typography>
                          <Stack
                            gap="20px"
                            sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
                          >
                            <Box
                              {...averageBoxProps}
                              sx={{
                                border: '1px solid #2061BC',
                                backgroundColor: '#E7F2F9',
                              }}
                            >
                              <Typography color="#2061BC">
                                Average Score
                              </Typography>
                              <Tooltip
                                title="Average score of other students in this quiz. "
                                arrow
                              >
                                <InfoIcon
                                  sx={{ fontSize: 15, color: '#334155' }}
                                />
                              </Tooltip>
                              <Typography color="#647488">:</Typography>
                              <Typography
                                fontWeight="600"
                                color={TP_COLORS.gray.darken}
                              >
                                {getAverageScore(topicData?.subject || '')}%
                              </Typography>
                            </Box>
                            <Box
                              {...averageBoxProps}
                              sx={{
                                border: '1px solid #8630F5',
                                backgroundColor: '#F2E8FF',
                              }}
                            >
                              <Typography color="#8630F5">
                                Average Time
                              </Typography>
                              <Tooltip
                                title="Average time of other student in this quiz"
                                arrow
                              >
                                <InfoIcon sx={infoIconStyles} />
                              </Tooltip>
                              <Typography color="#647488">:</Typography>
                              <Typography
                                fontWeight="600"
                                color={TP_COLORS.gray.darken}
                              >
                                {subTopicAverageTime}
                              </Typography>
                            </Box>
                          </Stack>

                          {submissionData.aiFeedback?.recommendation && (
                            <Box sx={gradientBoxStyles}>
                              <Typography fontWeight="600" marginBottom="10px">
                                Examiner Recommendation
                              </Typography>
                              <Typography fontSize="14px">
                                {submissionData.aiFeedback.recommendation}
                              </Typography>
                            </Box>
                          )}
                        </Card>
                        <Card sx={cardStyles}>
                          <Typography fontSize="18px" fontWeight="700">
                            Breakdown by Question
                          </Typography>
                          <QuestionListContainer
                            submission={submissionData}
                            selectedQuestion={selectedQuestion}
                            onQuestionClicked={handleQuestionChange}
                          />

                          {questionData &&
                          selectedQuestion &&
                          selectedQuestion.question === questionData._id ? (
                            <Box
                              sx={{
                                borderRadius: '20px',
                                padding: '20px',
                                border: '1px solid #E2E8F0',
                              }}
                            >
                              {/* QUESTION DETAIL VIEWER */}
                              <QuestionDetailViewer
                                selectedQuestion={selectedQuestion}
                                questionData={questionData}
                              />

                              {/* OPEN ENDED GRADE DATA VIEWER */}
                              {isOpenEndedQuestion &&
                              selectedQuestion.question === questionData._id ? (
                                <GradedSection
                                  selectedQuestion={selectedQuestion}
                                  questionData={questionData}
                                />
                              ) : null}

                              {/* DETAILED MARKING SCHEME VIEWER */}
                              {submissionData &&
                              questionData &&
                              selectedQuestion &&
                              selectedQuestion.question === questionData._id &&
                              !selectedQuestion.isOpenEnded ? (
                                <Fade in>
                                  <div style={{ marginBottom: '15px' }}>
                                    <DetailedMarkingSchemeViewer
                                      questionData={questionData}
                                      selectedQuestion={selectedQuestion}
                                      submissionData={submissionData}
                                    />
                                  </div>
                                </Fade>
                              ) : null}
                            </Box>
                          ) : (
                            <BasicLoader
                              containerProps={{
                                minHeight: '300px',
                                alignItems: 'center',
                              }}
                            />
                          )}
                        </Card>
                      </>
                    )}
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3.5}
                  lg={4}
                  sx={{ display: { xs: 'none', md: 'block' } }}
                >
                  <CompletionCard />
                </Grid>
              </Grid>
            </Fade>
          ) : (
            <BasicLoader loaderProps={{ style: { color: TP_COLORS.white } }} />
          )}
        </div>
      </Container>
      <div style={{ minHeight: '120px' }} />
    </Box>
  );
};

export default ViewQuizSubmission;
